import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';
import TablePagination, { DEFAULT_PAGE_SIZE } from './TablePagination';

const EMPTY_SUMMARY = {
    total_cards: 0,
    active: 0,
    returned: 0,
    total_balance: 0,
    expired: 0,
    zero_balance: 0
};

export default function MifareCardManagement() {
    const { t } = useTranslation();
    const [cards, setCards] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [totalItems, setTotalItems] = useState(0);
    const [gate, setGate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCard, setNewCard] = useState({
        card_uid: '',
        balance: 0,
        deposit_amount: 0,
        user_id: '',
        create_date: new Date().toISOString().split('T')[0],
        expire_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
        card_type: 'standard',
        status: 'active',
        notes: ''
    });
    const [editingCard, setEditingCard] = useState(null);
    const [topupAmount, setTopupAmount] = useState('');
    const [topupCardId, setTopupCardId] = useState(null);
    const fetchIdRef = useRef(0);

    const cardTypes = [
        { value: 'standard', label: '💳 Standard Card' },
        { value: 'premium', label: '👑 VIP Card' },
        { value: 'student', label: '🎓 Student Card' },
        { value: 'senior', label: '👴 Senior Card' },
        { value: 'employee', label: '👨‍💼 Employee Card' }
    ];

    const cardStatuses = [
        { value: 'active', label: '🟢 Active', color: 'bg-green-100 text-green-800' },
        { value: 'assigned', label: '📍 Assigned', color: 'bg-blue-100 text-blue-800' },
        { value: 'in_service', label: '🚀 In Service', color: 'bg-purple-100 text-purple-800' },
        { value: 'inactive', label: '⚪ Inactive', color: 'bg-gray-100 text-gray-800' },
        { value: 'suspended', label: '⏸️ Suspended', color: 'bg-yellow-100 text-yellow-800' },
        { value: 'expired', label: '🔴 Expired', color: 'bg-red-100 text-red-800' },
        { value: 'blocked', label: '⛔ Blocked', color: 'bg-red-100 text-red-800' },
        { value: 'return', label: '↩️ Returned', color: 'bg-orange-100 text-orange-800' }
    ];

    // Debounce search so typing doesn't hammer the API
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, pageSize]);

    const fetchCards = React.useCallback(async () => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            const params = {
                page: pageSize === 'all' ? 1 : currentPage,
                limit: pageSize === 'all' ? 'all' : pageSize
            };
            if (debouncedSearch) {
                params.search = debouncedSearch;
            }

            const response = await API.get('/admin/mifare-cards', { params });
            if (fetchId !== fetchIdRef.current) return;

            const payload = response.data;
            // Support both paginated object and legacy array
            if (Array.isArray(payload)) {
                setCards(payload);
                setTotalItems(payload.length);
                setSummary({
                    total_cards: payload.length,
                    active: payload.filter(c => c.status === 'active').length,
                    returned: payload.filter(c => c.status === 'return' || c.status === 'returned').length,
                    total_balance: payload.reduce((sum, c) => sum + (parseFloat(c.balance) || 0), 0),
                    expired: payload.filter(c => c.status === 'expired' || (c.expire_date && new Date(c.expire_date) < new Date())).length,
                    zero_balance: payload.filter(c => c.balance === 0 || c.balance === null).length
                });
            } else {
                setCards(payload.data || []);
                setTotalItems(payload.pagination?.total ?? (payload.data || []).length);
                setSummary({
                    ...EMPTY_SUMMARY,
                    ...(payload.summary || {})
                });
                if (payload.gate) setGate(payload.gate);
            }
        } catch (error) {
            if (fetchId !== fetchIdRef.current) return;
            toast.error(`❌ ${t('error_messages.load_mifare_cards_failed')}`);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    }, [t, currentPage, pageSize, debouncedSearch]);

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    const resetForm = () => {
        setNewCard({
            card_uid: '',
            balance: 0,
            deposit_amount: 0,
            user_id: '',
            create_date: new Date().toISOString().split('T')[0],
            expire_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            card_type: 'standard',
            status: 'active',
            notes: ''
        });
        setEditingCard(null);
    };

    const validateCardForm = () => {
        if (!newCard.card_uid.trim()) {
            toast.error(`❌ ${t('form_validation.card_uid_required')}`);
            return false;
        }
        if (newCard.balance < 0) {
            toast.error(`❌ ${t('form_validation.balance_not_negative')}`);
            return false;
        }
        if (new Date(newCard.expire_date) <= new Date(newCard.create_date)) {
            toast.error(`❌ ${t('form_validation.expire_date_after_create')}`);
            return false;
        }
        return true;
    };

    const addCard = async (e) => {
        e.preventDefault();
        if (!validateCardForm()) return;

        try {
            await API.post('/admin/mifare-cards', newCard);
            resetForm();
            setShowAddForm(false);
            toast.success(`✅ ${t('success_messages.mifare_card_added')}`);
            await fetchCards();
        } catch (error) {
            const message = error.response?.data?.message || error.message;
            toast.error(`❌ ${t('error_messages.add_mifare_card_failed')}: ${message}`);
        }
    };

    const updateCard = async (e) => {
        e.preventDefault();
        if (!validateCardForm()) return;

        try {
            console.log('🔄 Submitting MIFARE card update:', {
                id: editingCard.id,
                newCard: newCard,
                status: newCard.status
            });
            await API.put(`/admin/mifare-cards/${editingCard.id}`, newCard);
            resetForm();
            setShowAddForm(false);
            toast.success(`✅ ${t('success_messages.mifare_card_updated')}`);
            await fetchCards();
        } catch (error) {
            console.error('❌ MIFARE card update error:', error);
            const message = error.response?.data?.message || error.message;
            toast.error(`❌ ${t('error_messages.update_mifare_card_failed')}: ${message}`);
        }
    };

    const handleEdit = (card) => {
        console.log('✏️ Editing MIFARE card:', card);
        console.log('Card status:', card.status);
        setEditingCard(card);
        const editFormData = {
            ...card,
            create_date: card.create_date ? card.create_date.split('T')[0] : new Date().toISOString().split('T')[0],
            expire_date: card.expire_date ? card.expire_date.split('T')[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        console.log('Form data after processing:', editFormData);
        console.log('Form data status:', editFormData.status);
        setNewCard(editFormData);
        setShowAddForm(true);
    };

    const handleTopup = async (cardId) => {
        if (!topupAmount || parseFloat(topupAmount) <= 0) {
            toast.error(`❌ ${t('mifare.messages.topup_amount_required')}`);
            return;
        }

        try {
            await API.post(`/admin/mifare-cards/${cardId}/topup`, {
                amount: parseFloat(topupAmount),
                description: 'Admin Top-up'
            });

            setTopupAmount('');
            setTopupCardId(null);
            toast.success(`✅ ${t('mifare.messages.topup_success', { amount: parseFloat(topupAmount).toLocaleString() })}`);
            await fetchCards();
        } catch (error) {
            const message = error.response?.data?.message || error.message;
            toast.error(`❌ ${t('mifare.messages.topup_failed', { message })}`);
        }
    };

    const deleteCard = async (id, cardUid) => {
        if (window.confirm(t('mifare.messages.delete_confirm', { cardUid }))) {
            try {
                await API.delete(`/admin/mifare-cards/${id}`);
                toast.success(`✅ ${t('mifare.messages.delete_success', { cardUid })}`);
                await fetchCards();
            } catch (error) {
                toast.error(`❌ ${t('mifare.messages.delete_failed')}`);
            }
        }
    };

    const safePage = (() => {
        if (pageSize === 'all' || totalItems === 0) return 1;
        const totalPages = Math.max(1, Math.ceil(totalItems / Number(pageSize)) || 1);
        return Math.min(Math.max(1, currentPage), totalPages);
    })();

    const canEditCard = (card) => {
        if (!gate) return true;
        const cardLocation = (card.location || '').trim().toUpperCase();
        const currentGate = gate.trim().toUpperCase();
        return cardLocation === currentGate;
    };

    if (loading && cards.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                    💳 {t('mifare.title')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    {t('mifare.description')}
                </p>
            </div>

            {/* Header Actions */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder={`🔍 ${t('mifare.search_placeholder')}`}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                        ➕ {t('mifare.add_mifare_button')}
                    </button>

                    <button
                        onClick={fetchCards}
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                        🔄 {t('mifare.refresh_button')}
                    </button>
                </div>
            </div>

            {/* Add Card Form */}
            {showAddForm && (
                <div className="mb-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">
                        {editingCard ? `✏️ ${t('mifare.edit_card')}` : `➕ ${t('mifare.add_new_card')}`}
                    </h3>
                    <form onSubmit={editingCard ? updateCard : addCard} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Card UID
                            </label>
                            <input
                                type="text"
                                required
                                placeholder={t('mifare.placeholders.card_uid_example')}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.card_uid}
                                onChange={(e) => setNewCard({ ...newCard, card_uid: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.initial_balance')}
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-500 dark:text-white cursor-not-allowed"
                                value={newCard.balance}
                                readOnly
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                💰 Deposit Amount
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="50000"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-500 dark:text-white cursor-not-allowed"
                                value={newCard.deposit_amount}
                                readOnly
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.create_date')}
                            </label>
                            <input
                                type="date"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.create_date}
                                onChange={(e) => setNewCard({ ...newCard, create_date: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.expire_date')}
                            </label>
                            <input
                                type="date"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.expire_date}
                                onChange={(e) => setNewCard({ ...newCard, expire_date: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.card_type')}
                            </label>
                            <select
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.card_type}
                                onChange={(e) => setNewCard({ ...newCard, card_type: e.target.value })}
                            >
                                {cardTypes.map(ct => (
                                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.card_status')}
                            </label>
                            <select
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.status}
                                onChange={(e) => setNewCard({ ...newCard, status: e.target.value })}
                            >
                                {cardStatuses.map(cs => (
                                    <option key={cs.value} value={cs.value}>{cs.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.user_id_optional')}
                            </label>
                            <input
                                type="number"
                                placeholder="User ID"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.user_id}
                                onChange={(e) => setNewCard({ ...newCard, user_id: e.target.value })}
                            />
                        </div>

                        <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.form_labels.notes')}
                            </label>
                            <input
                                type="text"
                                placeholder={t('mifare.placeholders.notes_placeholder')}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                                value={newCard.notes}
                                onChange={(e) => setNewCard({ ...newCard, notes: e.target.value })}
                            />
                        </div>

                        <div className="md:col-span-3 flex gap-2">
                            <button
                                type="submit"
                                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded transition-colors duration-200"
                            >
                                {editingCard ? `✅ ${t('mifare.save_changes')}` : `✅ ${t('mifare.add_card_action')}`}
                            </button>
                            <button
                                type="button"
                                onClick={() => { resetForm(); setShowAddForm(false); }}
                                className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors duration-200"
                            >
                                ❌ {t('mifare.cancel')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Statistics Cards — from API summary */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {summary.total_cards}
                    </div>
                    <div className="text-blue-600 dark:text-blue-400 text-sm">💳 Total Cards</div>
                </div>

                <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                        {summary.active}
                    </div>
                    <div className="text-green-600 dark:text-green-400 text-sm">🟢 Active</div>
                </div>

                <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                        {summary.returned}
                    </div>
                    <div className="text-purple-600 dark:text-purple-400 text-sm">↩️ Returned</div>
                </div>

                <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
                        {Number(summary.total_balance || 0).toLocaleString()}
                    </div>
                    <div className="text-yellow-600 dark:text-yellow-400 text-sm">💸 Total Balance</div>
                </div>

                <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-800 dark:text-red-200">
                        {summary.expired}
                    </div>
                    <div className="text-red-600 dark:text-red-400 text-sm">🔴 Expired</div>
                </div>

                <div className="bg-orange-100 dark:bg-orange-900 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                        {summary.zero_balance}
                    </div>
                    <div className="text-orange-600 dark:text-orange-400 text-sm">⚪ Zero Balance</div>
                </div>
            </div>

            {/* Top-up Modal */}
            {topupCardId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
                        <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">
                            {t('mifare.topup_modal_title')}
                        </h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('mifare.topup_amount')}
                            </label>
                            <input
                                type="number"
                                min="1"
                                step="0.01"
                                placeholder={t('mifare.placeholders.topup_amount_placeholder')}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                                value={topupAmount}
                                onChange={(e) => setTopupAmount(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleTopup(topupCardId)}
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded transition-colors duration-200"
                            >
                                {t('mifare.topup_confirm')}
                            </button>
                            <button
                                onClick={() => { setTopupCardId(null); setTopupAmount(''); }}
                                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors duration-200"
                            >
                                {t('mifare.topup_cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cards Table */}
            <div className={`overflow-x-auto relative ${loading ? 'opacity-60' : ''}`}>
                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Card UID</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Serial No</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">📍 Location</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.type')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.status')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-right">{t('mifare.table_headers.balance')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-right">💰 Deposit</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">🚗 License Plate</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.user_id')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.create_date')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.expire_date')}</th>
                            <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('mifare.table_headers.management')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cards.length === 0 ? (
                            <tr>
                                <td colSpan="12" className="text-center p-8 text-gray-500 dark:text-gray-400">
                                    {t('mifare.messages.no_cards_found')}
                                </td>
                            </tr>
                        ) : (
                            cards.map((card, idx) => {
                                const isExpired = new Date(card.expire_date) < new Date();
                                const expiringSoon = new Date(card.expire_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                                const editable = canEditCard(card);

                                return (
                                    <tr key={`card-${card.id}-${card.card_uid}-${idx}`} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isExpired ? 'bg-red-50 dark:bg-red-900/20' : expiringSoon ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                        }`}>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 font-mono font-medium">
                                            <div>
                                                <div>{card.card_uid}</div>
                                                {card.notes && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{card.notes}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3">
                                            <div className="font-medium">{card.serial_no || '-'}</div>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                                            {card.location ? (
                                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                    editable
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {card.location}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                                {cardTypes.find(ct => ct.value === card.card_type)?.label || card.card_type}
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${cardStatuses.find(cs => cs.value === card.status)?.color || 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {cardStatuses.find(cs => cs.value === card.status)?.label || card.status}
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-right font-medium">
                                            <span className={`${card.balance > 0 ? 'text-green-600' : 'text-gray-500'
                                                }`}>
                                                {parseFloat(card.balance || 0).toLocaleString()} LAK
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-right font-medium">
                                            <span className="text-orange-600">
                                                {parseFloat(card.deposit_amount || 0).toLocaleString()} LAK
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                                            {card.license_plate ? (
                                                <div>
                                                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                                                        {card.license_plate}
                                                    </div>
                                                    {card.vehicle_owner && (
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                            {card.vehicle_owner}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                                            {card.user_id ? (
                                                <span className="text-blue-600 font-medium">#{card.user_id}</span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center text-sm">
                                            {card.create_date ? new Date(card.create_date).toLocaleDateString('en-GB', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3 text-center text-sm">
                                            <div className={isExpired ? 'text-red-600 font-bold' : expiringSoon ? 'text-yellow-600' : ''}>
                                                {card.expire_date ? new Date(card.expire_date).toLocaleDateString('en-GB', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                                                {isExpired && <div className="text-xs">{t('mifare.expired_already')}</div>}
                                                {!isExpired && expiringSoon && <div className="text-xs">{t('mifare.expiring_soon')}</div>}
                                            </div>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 p-3">
                                            <div className="flex justify-center space-x-1">
                                                <button
                                                    onClick={() => editable && handleEdit(card)}
                                                    disabled={!editable}
                                                    className={`px-2 py-1 rounded text-xs transition-colors duration-200 ${
                                                        editable
                                                            ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                                    }`}
                                                    title={editable
                                                        ? t('mifare.tooltips.edit_card')
                                                        : `Cannot edit — location (${card.location || '-'}) ≠ GATE (${gate})`}
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => deleteCard(card.id, card.card_uid)}
                                                    className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs transition-colors duration-200"
                                                    title={t('mifare.tooltips.delete_card')}
                                                >
                                                    🚮️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <TablePagination
                currentPage={safePage}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
                itemLabel="cards"
            />
        </div>
    );
}
